import { useContext, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { MenuComponent } from '../../_metronic/assets/ts/components'
import { DownloadButton } from './download'

import { setCompanySession, setProjectSession, getProjectSession, getCompanySession, usePermission, isValidHttpUrl, FullPageLoader } from './helper'
import Swal from 'sweetalert2'
import { companySession } from '../master/api'
import { callCreate, checkCallLock, dowloadFile, unlockCallList } from '../master/telecalling/api'
import { swlAlert } from './sweetAlert'
import Timer from './timer'
import CsvAprovedFileForm from './upload/csvApprovedPopup'
import { RightRedemLabel } from '../company-module/redemptionList/productMapping/catelouge/selectRedemLabel'
import { EditRedem } from '../company-module/redemptionList/productMapping/editSelectRedem'
import { deletUserPincode } from '../company-module/api'
import { CatlougeSkuOffer } from '../company-module/redemptionList/productMapping/catalugeSkuOffer'
import { useAuth } from '../modules/auth'
import { ComponentRequest } from './componentLayout'
import { unlockConsumerSurveyCall } from '../master/telecalling/custom/api'

// Unlocks a call if confirmed by the user
/**
 * handleLockClick
 *
 * Unlocks a call after user confirmation via SweetAlert2 dialog.
 * Updates UI to reflect lock/unlock status.
 *
 * @param {number|string} lead_id - The ID of the lead/call to unlock
 * @param {Function} setLoading - Function to set loading state
 */
const handleLockClick = async (lead_id, setLoading) => {
    const confirmResult = await Swal.fire({
        title: 'Are you sure?',
        text: 'Are you sure you want to unlock this call?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: "Confirm",
        cancelButtonText: "Cancel",
        reverseButtons: true,
        width: '500px',
        customClass: {
            confirmButton: 'btn btn-danger',
            cancelButton: 'btn btn-secondary'
        }
    });

    if (confirmResult.isConfirmed) {
        setLoading(true);
        try {
            const response = await unlockCallList({ calling_id: lead_id });
            if (response) {
                swlAlert(response);
                // Toggle lock/unlock icons
                const btnLock = document.getElementById("lock" + lead_id);
                const btnunLock = document.getElementById("unlock" + lead_id);
                btnLock?.classList.add('d-none');
                btnunLock?.classList.remove('d-none');
            }
        } finally {
            setLoading(false);
        }
    }
}

const handleSurveyUnlockClick = async (survey_id, setLoading) => {
    const confirmResult = await Swal.fire({
        title: 'Are you sure?',
        text: 'Are you sure you want to unlock this call?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: "Confirm",
        cancelButtonText: "Cancel",
        reverseButtons: true,
        width: '500px',
        customClass: {
            confirmButton: 'btn btn-danger',
            cancelButton: 'btn btn-secondary'
        }
    });

    if (confirmResult.isConfirmed) {
        setLoading(true);
        try {
            const response = await unlockConsumerSurveyCall({ calling_id: survey_id });
            if (response) {
                swlAlert(response);
                // Toggle lock/unlock icons
                const btnLock = document.getElementById("lock" + survey_id);
                const btnunLock = document.getElementById("unlock" + survey_id);
                btnLock?.classList.add('d-none');
                btnunLock?.classList.remove('d-none');
            }
        } finally {
            setLoading(false);
        }
    }
}
// Checks if another call is locked before navigating
/**
 * handleCheckLockClick
 *
 * Checks if another call is locked before navigating to the outbound list.
 * Shows a warning if a call is already locked.
 *
 * @param {number|string} call_id - The ID of the call to check
 * @param {Function} navigate - Navigation function
 * @param {Function} setLoading - Function to set loading state
 */
const handleCheckLockClick = async (call_id, navigate, setLoading) => {
    setLoading(true);
    try {
        const response = await checkCallLock({ calling_id: call_id });
        if (response) {
            await Swal.fire({
                title: 'You already have another call locked.',
                text: 'Please unlock it first',
                icon: 'warning',
                confirmButtonText: 'OK'
            });
        } else {
            navigate('/admin/telecalling/support/outbound_list/' + call_id);
        }
    } finally {
        setLoading(false);
    }
}

// Creates a new call and navigates to its page
/**
 * handleSearchCallClick
 *
 * Creates a new call and navigates to its outbound list page.
 * Shows an alert if creation fails.
 *
 * @param {Object} items - Data object for call creation
 * @param {Function} navigate - Navigation function
 * @param {Function} setLoading - Function to set loading state
 */
const handleSearchCallClick = async (items, navigate, setLoading) => {
    setLoading(true);
    try {
        const response = await callCreate({
            calling_type: 1,
            ref: items?.iirpl_sap_code,
            calling_generated_from: 1,
            project_id: items?.project_id,
            calling_purpose: "Manual"
        });

        if (response.code === 0) {
            swlAlert(response);
        } else {
            navigate('/admin/telecalling/support/outbound_list/' + response?.data?.ref_key);
        }
    } finally {
        setLoading(false);
    }
}

// Handles file download with confirmation
/**
 * handleDwonloadClick
 *
 * Handles file download after user confirmation via SweetAlert2 dialog.
 * Downloads CSV and updates UI for verification.
 *
 * @param {Object} item - Data object containing file info
 */
const handleDwonloadClick = async (item) => {
    const confirmResult = await Swal.fire({
        title: 'Are you sure?',
        text: 'Are you sure you want to download',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: "Confirm",
        cancelButtonText: "Cancel",
        reverseButtons: true,
        width: '500px',
        customClass: {
            confirmButton: 'btn btn-danger',
            cancelButton: 'btn btn-secondary'
        }
    });
    if (confirmResult.isConfirmed) {
        const response = await dowloadFile({ key: item?.file_verify_token })
        if (response) {
            // Download CSV file
            const blob = new Blob([response], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = item.file_source + '.csv';
            a.click();
            // Show verification key
            const btnLock = document.getElementById("verify" + item?.file_verify_token);
            btnLock.classList.remove('d-none')
        }
    }
}

// Project session management and navigation
export const ProjectSession = ({ items, classes }) => {
    const navigate = useNavigate();
    const projectExist = getProjectSession();
    const [loading, setLoading] = useState(false);

    // Handles switching to a project dashboard
    const manageProject = (items) => {

        (async () => {
            const willManage = await Swal.fire({
                title: "Are you sure?",
                text: "You will be redirected to the Project Dashboard.",
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Project Dashboard",
                cancelButtonText: "Cancel",
                reverseButtons: true,
                customClass: {
                    confirmButton: 'btn btn-danger',
                    cancelButton: 'btn btn-secondary'
                }
            });
            if (willManage.isConfirmed) {
                setLoading(true); // Show loader
                try {
                    const data = await companySession(0, items.id);
                    if (data.code === 1 && data?.data?.project) {
                        setProjectSession(data.data.project);
                        const project = data.data.project;
                        setTimeout(() => {
                            setLoading(false);
                            navigate(`/apps/company-management/structure/${project.ref_key}/${project.id}`)
                        }, 1000);
                    } else {
                        setLoading(false);
                        Swal.fire({
                            icon: 'error',
                            title: 'Switch failed',
                            text: data?.message || 'Could not switch to this project.',
                        });
                    }
                } catch (error) {
                    setLoading(false);
                    Swal.fire({
                        icon: 'error',
                        title: 'Switch failed',
                        text: error?.response?.data?.message || 'Could not switch to this project.',
                    });
                }
            }
        })();
    };

    // Render project session button based on current session
    return <>
        {projectExist?.id ?
            <>
                {Number(projectExist?.id) === Number(items.id) ?
                    <div className={classes} onClick={() => manageProject(items)} >
                        <span className="symbol-label fs-6 fw-semibold text-white  bg-success">
                            <i className="bi bi-eye-fill text-white fs-4 fw-semibold"></i>
                        </span>
                    </div>
                    : <div className={classes}>
                        <span className="symbol-label fs-6 fw-semibold text-white bg-danger ">
                            <i className="bi bi-eye-slash-fill text-white fs-4 fw-semibold"></i>
                        </span>
                    </div>
                }
            </>
            : <div className={classes} onClick={() => manageProject(items)} >
                <span className="symbol-label fs-6 fw-semibold text-white bg-dark bg-hover-success">
                    <i className="bi bi-eye-fill text-white fs-4 fw-semibold"></i>
                </span>
            </div>
        }
        {loading && (
            <FullPageLoader />
        )}
    </>
}

// Company session management and navigation
const CompanySession = ({ items, classes }) => {
    const navigate = useNavigate();
    const companyExist = getCompanySession();
    const [loading, setLoading] = useState(false);

    // Handles switching to a company dashboard
    const manageCompany = (items) => {
        (async () => {
            const willManage = await Swal.fire({
                title: "Are you sure?",
                text: "You will redirect company dashboard",
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Company Dashboard",
                cancelButtonText: "Cancel",
                reverseButtons: true,
                customClass: {
                    confirmButton: 'btn btn-danger',
                    cancelButton: 'btn btn-secondary'
                }
            });
            if (willManage.isConfirmed) {
                setLoading(true); // Show loader
                try {
                    const data = await companySession(items.id, 0);
                    if (data.code === 1 && data?.data?.company) {
                        setCompanySession(data.data.company);
                        setTimeout(() => {
                            setLoading(false);
                            navigate('/dashboard');
                        }, 1000);
                    } else {
                        setLoading(false);
                        Swal.fire({
                            icon: 'error',
                            title: 'Switch failed',
                            text: data?.message || 'Could not switch to this company.',
                        });
                    }
                } catch (error) {
                    setLoading(false);
                    Swal.fire({
                        icon: 'error',
                        title: 'Switch failed',
                        text: error?.response?.data?.message || 'Could not switch to this company.',
                    });
                }
            }
        })();
    };

    // Render company session button based on current session
    return <>
        {companyExist?.id ?
            <>
                {Number(companyExist?.id) === Number(items.id) ?
                    <div className={classes} onClick={() => manageCompany(items)} >
                        <span className="symbol-label fs-6 fw-semibold text-white  bg-success">
                            <i className="bi bi-eye-fill text-white fs-4 fw-semibold"></i>
                        </span>
                    </div>
                    : <div className={classes}>
                        <span className="symbol-label fs-6 fw-semibold text-white bg-danger ">
                            <i className="bi bi-eye-slash-fill text-white fs-4 fw-semibold"></i>
                        </span>
                    </div>
                }
            </>
            : <div className={classes} onClick={() => manageCompany(items)} >
                <span className="symbol-label fs-6 fw-semibold text-white bg-dark bg-hover-success">
                    <i className="bi bi-eye-fill text-white fs-4 fw-semibold"></i>
                </span>
            </div>
        }
        {loading && (
            <FullPageLoader />
        )}
    </>
}

// Deletes a user pincode after confirmation
/**
 * deletePincodeUser
 *
 * Deletes a user pincode after user confirmation via SweetAlert2 dialog.
 * Calls API and shows result alert.
 *
 * @param {number|string} id - The ID of the pincode to delete
 */
const deletePincodeUser = (id) => {
    (async () => {
        const willManage = await Swal.fire({
            title: "Are you sure?",
            text: "You want to delete this pincode",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Delete",
            cancelButtonText: "Cancel",
            confirmButtonColor: "#d33", // optional: red color for delete
        });

        if (willManage.isConfirmed) {
            deletUserPincode({ id: id }).then((d) => {
                swlAlert(d)
            })
        }
    })();
}

// Main cell component for user actions
const UserActionsCell = ({ items, action, id, link, select, lead_status, tooltip = [] }) => {
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const { currentUser } = useAuth()
    const { comRequest } = useContext(ComponentRequest)
    const permissions = usePermission(comRequest?.module, currentUser)
    const classes = 'symbol symbol-25px cursor-pointer symbol-circle me-2'

    // Reinitialize menu on mount
    useEffect(() => {
        MenuComponent.reinitialization()
    }, [])

    // Show loader if loading
    if (loading) {
        return <FullPageLoader />
    }

    // Render action buttons based on permissions and action type
    return (
        permissions.includes('edit') ?
            <>
                {/* Show file verification token if available */}
                {items?.file_verify_token &&
                    <span id={'verify' + items?.file_verify_token} class="d-none m-2 badge badge-success">{items?.file_verify_token}</span>
                }
                <div className='d-flex flex-row gap-2'>
                    {/* Render buttons for each action type */}
                    {action.includes('manage') &&
                        <>
                            <ProjectSession items={items} classes={classes} />
                        </>
                    }
                    {action.includes('company') &&
                        <>
                            <CompanySession items={items} classes={classes} />
                        </>
                    }
                    {action.includes('special_manage') &&
                        <Link className={classes} to={link} >
                            <span className="symbol-label fs-6 fw-semibold text-white bg-dark bg-hover-success">
                                <i className="bi bi-eye-fill text-white fs-4 fw-semibold"></i>
                            </span>
                        </Link>
                    }
                    {action.includes('Edit') &&
                        <>
                            <Link className={classes} to={link} data-toggle="tooltip" title={tooltip.map((tool) => { return tool.Edit })}>
                                <span className="symbol-label fs-6 fw-semibold text-white bg-dark bg-hover-success">
                                    <i className="bi bi-pencil-fill fs-4 text-white fw-semibold"></i>
                                </span>
                            </Link>
                        </>
                    }
                    {action.includes('qc') &&
                        <Link className={classes} to={link + '/qc/' + id}  >
                            <span className="symbol-label fs-6 fw-semibold text-white bg-dark bg-hover-success">
                                <i className="bi bi-pencil-fill text-white fs-4 fw-semibold"></i>
                            </span>
                        </Link>
                    }
                    {action.includes('upload') &&
                        <Link className={classes} to={link + '/upload/' + id}  >
                            <span className="symbol-label fs-6 fw-semibold text-white bg-dark bg-hover-success">
                                <i className="bi bi-upload text-white fs-4 fw-semibold"></i>
                            </span>
                        </Link>
                    }
                    {action.includes('auto') &&
                        <Link className={classes} to={link + '/auto/' + id}  >
                            <span className="symbol-label fs-6 fw-semibold text-white bg-dark bg-hover-success">
                                <i className="bi bi-pencil-fill  text-white fs-4 fw-semibold"></i>
                            </span>
                        </Link>
                    }
                    {action.includes('Catalog') &&
                        <Link className={classes} to={'/apps/company-management/redemption/mapping/' + items.id}
                            data-toggle="tooltip"
                            title={tooltip.map((tool) => { return tool?.Catalog })}
                        >
                            <span className="symbol-label fs-6 fw-semibold text-white bg-dark bg-hover-success">
                                <i className="bi bi-cart4 text-white fs-4 fw-semibold"></i>
                            </span>
                        </Link>
                    }
                    {/* Other actions are commented out for future use */}
                    {action.includes('validate') &&
                        <Link className={classes} to={link + '/validate/' + id}  >
                            <span className="symbol-label fs-6 fw-semibold text-white bg-dark bg-hover-success">
                                <i className="bi bi-check-all text-white fs-4 fw-semibold"></i>
                            </span>
                        </Link>
                    }
                    {action.includes('Select') &&
                        <Link className={classes} to={link} >
                            <span className="symbol-label fs-6 fw-semibold text-white bg-dark bg-hover-success">
                                <i className="fa fa-plus text-white fs-4 fw-semibold"></i>
                            </span>
                        </Link>
                    }
                    {action.includes('FormSelect') &&
                        <Link className={classes} to={select}  >
                            <span className="symbol-label fs-6 fw-semibold text-white bg-dark bg-hover-success">
                                <i className="fa fa-plus text-white fs-4 fw-semibold"></i>
                            </span>
                        </Link>
                    }
                    {/* Download, call, and other action buttons */}
                    {action.includes('Download') && <> <DownloadButton DownloadApi={''} className="btn btn-primary w-100px p-2 btn-sm me-3" /></>}
                    {action.includes('CallClose') && (
                        <Link className={classes} to={link}>
                            <span className="symbol-label fs-6 fw-semibold text-white bg-dark bg-hover-success">
                                <i className="bi bi-eye-fill text-white fs-5 fw-semibold"></i>
                            </span>
                        </Link>
                    )}
                    {action.includes('ActiveUser') && (
                        <>
                            <Link className={classes} to={'/apps/company-management/activity/user/' + items.id}  >
                                <span className="symbol-label fs-6 fw-semibold text-white bg-dark bg-hover-success">
                                    <i className="bi bi-person-fill text-white fs-5 fw-semibold"></i>
                                </span>
                            </Link>
                        </>
                    )}
                    {/* Call actions with lock status */}
                    {action.includes('Call') && (
                        lead_status === 1 ? (
                            <div className={classes}>
                                <span className="symbol-label fs-6 fw-semibold text-white bg-dark bg-hover-success">
                                    <i className="bi bi-lock-fill text-white fs-5 fw-semibold"></i>
                                </span>
                            </div>
                        ) : (
                            <Link className={classes} to={link}>
                                <span className="symbol-label fs-6 fw-semibold text-white bg-dark bg-hover-success">
                                    <i className="bi bi-telephone-fill text-white fs-5 fw-semibold"></i>
                                </span>
                            </Link>
                        )
                    )}
                    {action.includes('SupportCall') && (
                        items?.lock_status === 1 ? (
                            <div className={classes}>
                                <span className="symbol-label fs-6 fw-semibold text-white bg-dark bg-hover-success">
                                    <i className="bi bi-lock-fill text-white fs-5 fw-semibold"></i>
                                </span>
                            </div>
                        ) : (
                            <div onClick={() => handleCheckLockClick(items?.calling_id, navigate, setLoading)}>
                                <div className={classes}>
                                    <span className="symbol-label fs-6 fw-semibold text-white bg-dark bg-hover-success bg-hover-success">
                                        <i id={'loadingd' + items?.calling_id} className="bi bi-telephone-fill text-white fs-5 fw-semibold"></i>
                                    </span>
                                </div>
                            </div>
                        )
                    )}
                    {action.includes('SearchCall') && (
                        <>
                            <div onClick={() => handleSearchCallClick(items, navigate, setLoading)}
                                className="symbol symbol-25px cursor-pointer symbol-circle me-2">
                                <span className="symbol-label fs-6 fw-semibold text-white bg-dark bg-hover-success">
                                    <i className="bi bi-telephone-plus-fill text-white fs-5 fw-semibold"></i>
                                </span>
                            </div>
                        </>
                    )}
                    {/* Timer actions for recall */}
                    {action.includes('ReCall') && (
                        <Timer date={items?.calling_update_at} lead_status={lead_status} classes={classes} link={link} items={items} navigate={navigate} />
                    )}
                    {action.includes('spportReCall') && (
                        <Timer date={items?.calling_update_at} lead_status={lead_status} classes={classes} link={link} items={items} navigate={navigate} handleCheckLockClick={handleCheckLockClick} setLoading={setLoading} />
                    )}
                    {action.includes('leadReCall') && (
                        <Timer date={items?.calling_update_at} lead_status={lead_status} classes={classes} link={link} items={items} navigate={navigate} handleCheckLockClick={handleCheckLockClick} setLoading={setLoading} />
                    )}
                    {action.includes('CallInProgress') && (
                        lead_status === 1 ? (
                            <div onClick={() => handleLockClick(id, setLoading)}>
                                <div className={classes}>
                                    <span className="symbol-label fs-6 fw-semibold text-white bg-dark bg-hover-success bg-hover-success">
                                        <i id={'lock' + id} className="bi bi-lock-fill text-white fs-5 fw-semibold"></i>
                                        <i id={'unlock' + id} className="d-none bi bi-unlock-fill text-white fs-5 fw-semibold"></i>
                                    </span>
                                </div>
                            </div>
                        ) : (
                            <Link className={classes} to={link}>
                                <span className="symbol-label fs-6 fw-semibold text-white bg-success">
                                    <i className="bi bi-telephone-fill text-white fs-5 fw-semibold"></i>
                                </span>
                            </Link>
                        )
                    )}
                    {action.includes('ConsumerSurveyUnlock') && (

                        <div onClick={() => handleSurveyUnlockClick(id, setLoading)}>
                            <div className={classes}>
                                <span className="symbol-label fs-6 fw-semibold text-white bg-dark bg-hover-success bg-hover-success">
                                    <i id={'lock' + id} className="bi bi-lock-fill text-white fs-5 fw-semibold"></i>
                                    <i id={'unlock' + id} className="d-none bi bi-unlock-fill text-white fs-5 fw-semibold"></i>
                                </span>
                            </div>
                        </div>
                    )
                    }
                    {/* Redemption catalogue actions */}
                    {action.includes('add-redemption-catalouge') &&
                        <><RightRedemLabel row={items} /></>
                    }
                    {action.includes('edit-redemption-catalouge') &&
                        <>
                            <EditRedem row={items} />
                            {items?.redemption_catalogues_special_offers.length > 0 &&
                                <CatlougeSkuOffer row={items} />
                            }
                        </>
                    }
                    {/* File approval and download actions */}
                    {action.includes('uploaded_file_approved') &&
                        <>
                            {permissions.includes("download") ?
                                <>
                                    {isValidHttpUrl(items?.file_result_csv) ?
                                        <div href={items?.file_result_csv} download>
                                            <div className={classes}>
                                                <span className="symbol-label fs-6 fw-semibold text-white bg-dark bg-hover-success bg-hover-success">
                                                    <i className="bi bi-cloud-download-fill text-white fs-5 fw-semibold"></i>
                                                </span>
                                            </div>
                                        </div>
                                        : <div onClick={() => handleDwonloadClick(items)}>
                                            <div className={classes}>
                                                <span className="symbol-label fs-6 fw-semibold text-white bg-dark bg-hover-success bg-hover-success">
                                                    <i className="bi bi-cloud-download-fill text-white fs-5 fw-semibold"></i>
                                                </span>
                                            </div>
                                        </div>
                                    }

                                </>
                                :
                                <div className='badge badge-light-danger fw-semibolder'>Permission Denied</div>
                            }
                            {Number(items?.status) === 0 ?
                                <CsvAprovedFileForm id={items?.id} url={link} />
                                : <div className={classes}>
                                    <span className={`symbol-label fs-6 fw-semibold text-white  ${Number(items?.status) === 1 ? 'bg-success' : 'bg-danger'}`}>
                                        <i className="bi bi-database-fill-lock text-white fs-5 fw-semibold"></i>
                                    </span>
                                </div>
                            }
                        </>
                    }
                    {/* KYC and ledger actions */}
                    {action.includes('KycUser') &&
                        <Link className={classes} to={'/admin/kyc/user'}>
                            <span className="symbol-label fs-6 fw-semibold text-white bg-dark">
                                <i class="bi bi-person-check text-white fs-5 fw-semibold"></i>
                            </span>
                        </Link>
                    }
                    {action.includes('addKyc') &&
                        <Link className={classes} to={`/kyc/bankpancard-details/${items?.iirpl_sap_code}`}>
                            <span className="symbol-label fs-6 fw-semibold text-white bg-dark">
                                <i class="bi bi-person-bounding-box text-white fs-5 fw-semibold"></i>
                            </span>
                        </Link>
                    }
                    {action.includes('Ledger') &&
                        <Link className={classes} to={`/apps/company-management/user_ledger/${items?.iirpl_sap_code}/${items?.project_id}`}>
                            <span className="symbol-label fs-6 fw-semibold text-white bg-dark">
                                <i class="bi bi-journal-text text-white fs-5 fw-semibold"></i>
                            </span>
                        </Link>
                    }
                    {action.includes('userDocs') &&
                        <Link className={classes} to={link}>
                            <span className="symbol-label fs-6 fw-semibold text-white bg-dark bg-hover-success">
                                <i className="bi bi-eye-fill text-white fs-5 fw-semibold"></i>
                            </span>
                        </Link>
                    }
                    {action.includes('View') &&
                        <Link className={classes} to={link} >
                            <span className="symbol-label fs-6 fw-semibold text-white bg-dark bg-hover-success">
                                <i className="bi bi-eye-fill fs-4 text-white fw-semibold"></i>
                            </span>
                        </Link>
                    }
                    {/* Delete user pincode */}
                    {action.includes('Delete') &&
                        <div onClick={() => deletePincodeUser(items?.id)} class="symbol symbol-25px cursor-pointer symbol-circle me-2"     >
                            <span class="symbol-label fs-6 fw-semibold text-white bg-dark bg-hover-success">
                                <i class="bi bi-trash-fill fs-5 text-white fw-semibold"></i>
                            </span>
                        </div>
                    }
                    {/* Offer edit and sub-offer actions */}
                    {action.includes('Offer Edit') &&
                        <Link className='btn btn-sm btn-secondary me-1' to={link}>
                            Edit <i className="bi bi-pencil-square ms-1"></i>
                        </Link>
                    }
                    {action.includes('Sub Offer') &&
                        <Link className='btn  btn-sm btn-light-danger' to={`/apps/offer-management/project_scheme/sub-offer/${items?.id}`}>
                            Sub Offer <i className="bi bi-arrow-right fw-bold"></i>
                        </Link>
                    }
                </div>
            </>
            :
            <div className='badge badge-light-danger fw-semibolder'>Permission Denied</div>
    )
}

export { UserActionsCell }

